/**
 * Custom hook for managing contact form state and interactions
 * Handles form fields, validation, submission, and notifications
 * @module hooks/useContactForm
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { DropdownOption } from '../../components/common/Dropdown';
import { sendContactMessage } from '../../services/contact/contactService';
import { validateContactForm, sanitizePhoneInput, type ContactFormState } from '../../utils/ContactUs/contactUsValidation';
import { 
  DEFAULT_DROPDOWN_OPTION, 
  ERROR_MESSAGES, 
  SUCCESS_MESSAGES,
  FIELD_LIMITS,
  TIMEOUTS,
} from '../../constants/contact.constants';
import type { FieldValidationError, ContactDetails, ContactFormData, ContactFormField } from '../../types/contact.types';

interface UseContactFormOptions {
  /** User details to populate read-only fields */
  userDetails: ContactDetails | null;
  /** Callback when region changes (to load centers) */
  onRegionChange?: (regionCode: string) => Promise<void> | void;
  /** Callback when center changes (to load topics) */
  onCenterChange?: (regionCode: string, centerCode: string) => Promise<void> | void;
  /** Callback when form needs to scroll to top */
  onScrollToTop?: () => void;
}

interface UseContactFormReturn {
  // Form field values
  selectedRegion: DropdownOption;
  selectedCenter: DropdownOption;
  selectedTopic: DropdownOption;
  teamNameOrUbtId: string;
  phoneAreaCode: string;
  phoneNumber: string;
  comments: string;
  email: string;
  
  // Validation and messages
  validationErrors: FieldValidationError[];
  hasAttemptedSubmit: boolean;
  successMessage: string;
  errorMessage: string;
  
  // Loading state
  isSubmitting: boolean;
  
  // Field handlers
  setSelectedRegion: (option: DropdownOption) => void;
  setSelectedCenter: (option: DropdownOption) => void;
  setSelectedTopic: (option: DropdownOption) => void;
  setTeamNameOrUbtId: (value: string) => void;
  setPhoneAreaCode: (value: string) => void;
  setPhoneNumber: (value: string) => void;
  setComments: (value: string) => void;
  setEmail: (value: string) => void;
  
  // Form actions
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleClear: () => void;
  clearMessages: () => void;
  
  // Current form state for validation
  formState: ContactFormState;
}

/**
 * Custom hook to manage contact form state and logic
 * Provides complete form management with validation and submission
 */
export const useContactForm = ({
  userDetails,
  onRegionChange,
  onCenterChange,
  onScrollToTop,
}: UseContactFormOptions): UseContactFormReturn => {
  // Form field state
  const [regionState, setRegionState] = useState<DropdownOption>(DEFAULT_DROPDOWN_OPTION);
  const [centerState, setCenterState] = useState<DropdownOption>(DEFAULT_DROPDOWN_OPTION);
  const [topicState, setTopicState] = useState<DropdownOption>(DEFAULT_DROPDOWN_OPTION);
  const [teamNameOrUbtId, setTeamNameOrUbtId] = useState('');
  const [phoneAreaCodeState, setPhoneAreaCodeState] = useState('');
  const [phoneNumberState, setPhoneNumberState] = useState('');
  const [comments, setComments] = useState('');
  const [email, setEmail] = useState('');
  
  // Validation and messages
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Update email when user details are loaded
   */
  useEffect(() => {
    if (userDetails?.email) {
      setEmail(userDetails.email);
    }
  }, [userDetails]);

  /**
   * Memoized form state for validation
   */
  const formState = useMemo<ContactFormState>(() => ({
    selectedRegion: regionState,
    selectedCenter: centerState,
    selectedTopic: topicState,
    teamNameOrUbtId,
    email,
    phoneAreaCode: phoneAreaCodeState,
    phoneNumber: phoneNumberState,
    comments,
  }), [regionState, centerState, topicState, teamNameOrUbtId, email, phoneAreaCodeState, phoneNumberState, comments]);

  /**
   * Clear validation error for a specific field
   */
  const clearFieldError = useCallback((fieldName: ContactFormField) => {
    setValidationErrors(prevErrors => 
      prevErrors.filter(error => error.field !== fieldName)
    );
  }, []);

  /**
   * Auto-dismiss messages after timeout
   */
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, TIMEOUTS.MESSAGE_AUTO_DISMISS);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  /**
   * Scroll to top when messages appear
   */
  useEffect(() => {
    if ((successMessage || errorMessage) && onScrollToTop) {
      onScrollToTop();
    }
  }, [successMessage, errorMessage, onScrollToTop]);

  /**
   * Handle region selection change
   */
  const setSelectedRegion = useCallback((option: DropdownOption) => {
    setRegionState(option);
    setCenterState(DEFAULT_DROPDOWN_OPTION);
    setTopicState(DEFAULT_DROPDOWN_OPTION);
    clearFieldError('region');
    
    if (option.value && onRegionChange) {
      onRegionChange(option.value);
    }
  }, [onRegionChange, clearFieldError]);

  /**
   * Handle center selection change
   */
  const setSelectedCenter = useCallback((option: DropdownOption) => {
    setCenterState(option);
    setTopicState(DEFAULT_DROPDOWN_OPTION);
    clearFieldError('center');
    
    if (option.value && regionState.value && onCenterChange) {
      onCenterChange(regionState.value, option.value);
    }
  }, [regionState, onCenterChange, clearFieldError]);

  /**
   * Handle topic selection change
   */
  const handleSetSelectedTopic = useCallback((option: DropdownOption) => {
    setTopicState(option);
    clearFieldError('topic');
  }, [clearFieldError]);

  /**
   * Handle team name change
   */
  const handleSetTeamNameOrUbtId = useCallback((value: string) => {
    setTeamNameOrUbtId(value);
    clearFieldError('teamNameOrUbtId');
  }, [clearFieldError]);

  /**
   * Handle email change
   */
  const handleSetEmail = useCallback((value: string) => {
    setEmail(value);
    clearFieldError('email');
  }, [clearFieldError]);

  /**
   * Handle comments change
   */
  const handleSetComments = useCallback((value: string) => {
    setComments(value);
    clearFieldError('comments');
  }, [clearFieldError]);

  /**
   * Handle phone area code input with sanitization
   */
  const setPhoneAreaCode = useCallback((value: string) => {
    const sanitized = sanitizePhoneInput(value, FIELD_LIMITS.PHONE_AREA_CODE_LENGTH);
    setPhoneAreaCodeState(sanitized);
    clearFieldError('phoneAreaCode');
  }, [clearFieldError]);

  /**
   * Handle phone number input with sanitization
   */
  const setPhoneNumber = useCallback((value: string) => {
    const sanitized = sanitizePhoneInput(value, FIELD_LIMITS.PHONE_NUMBER_LENGTH);
    setPhoneNumberState(sanitized);
    clearFieldError('phoneNumber');
  }, [clearFieldError]);

  /**
   * Clear all form fields and reset email to initial value from userDetails
   */
  const clearFormFields = useCallback(() => {
    setRegionState(DEFAULT_DROPDOWN_OPTION);
    setCenterState(DEFAULT_DROPDOWN_OPTION);
    setTopicState(DEFAULT_DROPDOWN_OPTION);
    setTeamNameOrUbtId('');
    setPhoneAreaCodeState('');
    setPhoneNumberState('');
    setComments('');
    setEmail(userDetails?.email ?? ''); // Reset email to initial value
    setValidationErrors([]);
    setHasAttemptedSubmit(false);
  }, [userDetails]);

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  /**
   * Handle clear button click
   */
  const handleClear = useCallback(() => {
    clearFormFields();
    clearMessages();
  }, [clearFormFields, clearMessages]);

  /**
   * Handle form submission
   * Validates the form and sends the contact message to the API.
   * Phone fields (phoneAreaCode and phone) are only included in the payload
   * if they contain values, as they are optional fields.
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    clearMessages();

    // Validate form
    const errors = validateContactForm(formState);
    setValidationErrors(errors);

    if (errors.length > 0) {
      if (onScrollToTop) {
        onScrollToTop();
      }
      return;
    }

    // Submit form
    if (!userDetails) {
      setErrorMessage(ERROR_MESSAGES.LOAD_INITIAL_DATA_FAILED);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Extract description without code for regionDesc
      // Label format is "CODE NAME" (e.g., "NAC Northern California")
      const regionDescWithoutCode = regionState.label.split(' ').slice(1).join(' ');
      
      // Construct base form data
      const formData: ContactFormData = {
        nuid: userDetails.nuid,
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        email,
        regionCode: regionState.value,
        regionDesc: regionDescWithoutCode,
        centerCode: centerState.value,
        centerDesc: centerState.label,
        ubtNameId: teamNameOrUbtId,
        feedbackType: topicState.label,
        comments,
      };

      // Only include phone fields if they have values
      if (phoneAreaCodeState.trim()) {
        formData.phoneAreaCode = phoneAreaCodeState;
      }
      if (phoneNumberState.trim()) {
        formData.phone = phoneNumberState;
      }

      const response = await sendContactMessage(formData);
      
      if (response.success) {
        // Clear validation errors and form state before showing success message
        setValidationErrors([]);
        setHasAttemptedSubmit(false);
        setSuccessMessage(SUCCESS_MESSAGES.MESSAGE_SENT);
        clearFormFields();
      } else {
        setErrorMessage(response.message || ERROR_MESSAGES.SEND_MESSAGE_FAILED);
      }
    } catch (error) {
      const errorMsg = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? ERROR_MESSAGES.SEND_MESSAGE_FAILED;
      setErrorMessage(errorMsg);
      console.error('Error sending contact message:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formState,
    userDetails,
    email,
    regionState,
    centerState,
    topicState,
    phoneAreaCodeState,
    phoneNumberState,
    teamNameOrUbtId,
    comments,
    onScrollToTop,
    clearFormFields,
    clearMessages,
  ]);

  return {
    // Form fields
    selectedRegion: regionState,
    selectedCenter: centerState,
    selectedTopic: topicState,
    teamNameOrUbtId,
    phoneAreaCode: phoneAreaCodeState,
    phoneNumber: phoneNumberState,
    comments,
    email,
    
    // Validation and messages
    validationErrors,
    hasAttemptedSubmit,
    successMessage,
    errorMessage,
    
    // Loading
    isSubmitting,
    
    // Field setters
    setSelectedRegion,
    setSelectedCenter,
    setSelectedTopic: handleSetSelectedTopic,
    setTeamNameOrUbtId: handleSetTeamNameOrUbtId,
    setPhoneAreaCode,
    setPhoneNumber,
    setComments: handleSetComments,
    setEmail: handleSetEmail,
    
    // Actions
    handleSubmit,
    handleClear,
    clearMessages,
    
    // Form state
    formState,
  };
};
